import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ClickAwayListener } from '@material-ui/core';

import * as Icons from '@material-ui/icons';

import history from '~/services/history';
import { formatMessage } from '~/i18n/translate';
import { AppStateType } from '~/store/modules/root-reducer';
import * as routingActions from '~/store/modules/routing/routing-actions';
import { AIRouteJobStatus } from '~/store/modules/routing/routing-interfaces';

import * as S from './styles';

const LEAVE_ANIMATION_MS = 220;

const STATUS_CONFIG: Record<AIRouteJobStatus, { label: string; hint: string; Icon: React.ElementType }> = {
  queued: { label: 'routingAiJobStatusQueued', hint: 'routingAiJobStatusQueuedHint', Icon: Icons.Schedule },
  processing: {
    label: 'routingAiJobStatusProcessing',
    hint: 'routingAiJobStatusProcessingHint',
    Icon: Icons.Autorenew
  },
  completed: {
    label: 'routingAiJobStatusCompleted',
    hint: 'routingAiJobStatusCompletedHint',
    Icon: Icons.CheckCircleOutline
  },
  failed: { label: 'routingAiJobStatusFailed', hint: 'routingAiJobStatusFailedHint', Icon: Icons.ErrorOutline }
};

const FAB_ICON: Record<AIRouteJobStatus, React.ElementType> = {
  queued: Icons.MapOutlined,
  processing: Icons.MapOutlined,
  completed: Icons.Check,
  failed: Icons.PriorityHigh
};

const formatTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : null;

const AiRouteJobWidget: React.FC = () => {
  const dispatch = useDispatch();
  const job = useSelector((state: AppStateType) => state.routing.aiRouteJob);

  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [hasUnseenResult, setHasUnseenResult] = useState(false);
  const previousStatus = useRef<AIRouteJobStatus | null>(null);

  useEffect(() => {
    dispatch(routingActions.restoreAIRouteJob());
  }, [dispatch]);

  useEffect(() => {
    const status = job?.status ?? null;
    const finished = status === 'completed' || status === 'failed';

    if (finished && previousStatus.current && previousStatus.current !== status && !open) {
      setHasUnseenResult(true);
    }

    previousStatus.current = status;
  }, [job?.status, open]);

  useEffect(() => {
    if (!job) {
      setOpen(false);
      setLeaving(false);
      setHasUnseenResult(false);
    }
  }, [job]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!job) return null;

  const { label, hint, Icon } = STATUS_CONFIG[job.status];
  const FabIcon = FAB_ICON[job.status];
  const isPending = job.status === 'queued' || job.status === 'processing';
  const isPollingStopped = isPending && !!job.pollingStopped;
  const isRunning = isPending && !job.pollingStopped;
  const isCompleted = job.status === 'completed';
  const lastChecked = formatTime(job.lastCheckedAt);

  const dismiss = (navigateToList: boolean) => {
    setOpen(false);
    setLeaving(true);

    if (navigateToList) history.push('/register/routing/list');

    setTimeout(() => dispatch(routingActions.clearAIRouteJob()), LEAVE_ANIMATION_MS);
  };

  const toggle = () => {
    setOpen(value => !value);
    setHasUnseenResult(false);
  };

  return (
    <ClickAwayListener onClickAway={() => open && setOpen(false)}>
      <S.Root $leaving={leaving}>
        <S.Panel $open={open} role="dialog" aria-hidden={!open} aria-label={formatMessage('routingAiJobWidgetTitle')}>
          <S.PanelHeader>
            <S.PanelTitle>{formatMessage('routingAiJobWidgetTitle')}</S.PanelTitle>
            <S.CloseButton
              type="button"
              onClick={() => setOpen(false)}
              aria-label={formatMessage('routingAiJobWidgetClose')}
              tabIndex={open ? 0 : -1}
            >
              <Icons.Close />
            </S.CloseButton>
          </S.PanelHeader>

          <S.StatusCard
            type="button"
            $status={job.status}
            $clickable={isCompleted}
            disabled={!isCompleted}
            onClick={() => isCompleted && dismiss(true)}
            tabIndex={open && isCompleted ? 0 : -1}
          >
            <S.StatusIcon $status={job.status} key={job.status}>
              <Icon />
            </S.StatusIcon>

            <S.StatusText>
              <S.StatusLabel $status={job.status}>{formatMessage(label)}</S.StatusLabel>
              <S.StatusHint>{formatMessage(hint)}</S.StatusHint>
              {job.status === 'queued' && job.queuePosition != null && (
                <S.StatusHint>
                  {formatMessage('routingAiJobStatusQueuePosition', { position: job.queuePosition })}
                </S.StatusHint>
              )}
            </S.StatusText>

            {isCompleted && (
              <S.Arrow data-arrow>
                <Icons.ChevronRight />
              </S.Arrow>
            )}

            {isRunning && <S.ProgressTrack />}
          </S.StatusCard>

          <S.Footer>
            <span>
              {isRunning &&
                (lastChecked
                  ? formatMessage('routingAiJobLastChecked', { time: lastChecked })
                  : formatMessage('routingAiJobWaitingFirstCheck'))}
              {isPollingStopped && formatMessage('routingAiJobPollingStopped')}
            </span>
            <S.FooterActions>
              {isRunning && (
                <S.TextButton
                  type="button"
                  onClick={() => dispatch(routingActions.stopAIRouteJobPolling())}
                  tabIndex={open ? 0 : -1}
                >
                  {formatMessage('routingAiJobStopPolling')}
                </S.TextButton>
              )}
              {isPollingStopped && (
                <S.TextButton
                  type="button"
                  onClick={() => dispatch(routingActions.resumeAIRouteJobPolling())}
                  tabIndex={open ? 0 : -1}
                >
                  {formatMessage('routingAiJobResumePolling')}
                </S.TextButton>
              )}
              <S.TextButton type="button" onClick={() => dismiss(false)} tabIndex={open ? 0 : -1}>
                {formatMessage('routingAiJobDismiss')}
              </S.TextButton>
            </S.FooterActions>
          </S.Footer>
        </S.Panel>

        <S.Fab
          type="button"
          $status={job.status}
          onClick={toggle}
          aria-expanded={open}
          aria-label={formatMessage('routingAiJobWidgetOpen')}
        >
          <FabIcon key={job.status} />
          {hasUnseenResult && <S.Badge />}
        </S.Fab>
      </S.Root>
    </ClickAwayListener>
  );
};

export default AiRouteJobWidget;
